import React from 'react'
import { cn } from '../../utils/cn'

export interface CardProps extends React.HTMLAttributes<HTMLDivElement> {
  variant?: 'default' | 'outline' | 'glass' | 'gradient'
  hover?: boolean
  clickable?: boolean
  padding?: 'none' | 'sm' | 'md' | 'lg'
  overflow?: 'visible' | 'hidden' | 'auto'
}

export interface CardHeaderProps extends React.HTMLAttributes<HTMLDivElement> {
  title?: string
  description?: string
  action?: React.ReactNode
}

export interface CardContentProps extends React.HTMLAttributes<HTMLDivElement> {}

export interface CardFooterProps extends React.HTMLAttributes<HTMLDivElement> {
  justify?: 'start' | 'center' | 'end' | 'between'
}

const Card = React.forwardRef<HTMLDivElement, CardProps>(({
  variant = 'default',
  hover = true,
  clickable = false,
  padding = 'md',
  overflow = 'hidden',
  className,
  children,
  ...props
}, ref) => {
  const baseStyles = cn(
    'rounded-xl transition-all duration-200',
    'border',
    overflow === 'hidden' && 'overflow-hidden',
    overflow === 'auto' && 'overflow-auto',
    clickable && 'cursor-pointer active:scale-98',
    className
  )

  const variantStyles = {
    default: cn(
      'bg-surface-light dark:bg-surface-dark',
      'border-secondary-200 dark:border-secondary-700',
      'shadow-card',
      hover && 'hover:shadow-card-hover hover:border-secondary-300 dark:hover:border-secondary-600'
    ),
    outline: cn(
      'bg-transparent',
      'border-secondary-300 dark:border-secondary-600',
      hover && 'hover:border-secondary-400 dark:hover:border-secondary-500'
    ),
    glass: cn(
      'backdrop-blur-sm bg-white/70 dark:bg-black/30',
      'border-white/20 dark:border-white/10',
      'shadow-sm',
      hover && 'hover:bg-white/80 dark:hover:bg-black/40'
    ),
    gradient: cn(
      'bg-gradient-to-br from-primary-50 to-secondary-50 dark:from-primary-900/20 dark:to-secondary-900/20',
      'border-primary-200 dark:border-primary-800',
      'shadow-primary/10',
      hover && 'hover:shadow-primary/20'
    ),
  }

  const paddingStyles = {
    none: '',
    sm: 'p-4',
    md: 'p-6',
    lg: 'p-8',
  }

  return (
    <div
      ref={ref}
      className={cn(
        baseStyles,
        variantStyles[variant],
        paddingStyles[padding]
      )}
      {...props}
    >
      {children}
    </div>
  )
})

Card.displayName = 'Card'

// Card Header Component
const CardHeader = React.forwardRef<HTMLDivElement, CardHeaderProps>(({
  title,
  description,
  action,
  className,
  children,
  ...props
}, ref) => {
  return (
    <div
      ref={ref}
      className={cn(
        'flex flex-col space-y-1.5 pb-4 border-b border-secondary-200 dark:border-secondary-700',
        className
      )}
      {...props}
    >
      <div className="flex items-start justify-between gap-4">
        <div className="flex-1 min-w-0">
          {title && (
            <h3 className="text-lg font-semibold text-secondary-900 dark:text-white truncate">
              {title}
            </h3>
          )}
          {description && (
            <p className="text-sm text-secondary-500 dark:text-secondary-400 mt-1">
              {description}
            </p>
          )}
        </div>
        {action && <div className="flex-shrink-0">{action}</div>}
      </div>
      {children}
    </div>
  )
})

CardHeader.displayName = 'CardHeader'

// Card Content Component
const CardContent = React.forwardRef<HTMLDivElement, CardContentProps>(({
  className,
  children,
  ...props
}, ref) => {
  return (
    <div
      ref={ref}
      className={cn('py-4', className)}
      {...props}
    >
      {children}
    </div>
  )
})

CardContent.displayName = 'CardContent'

// Card Footer Component
const CardFooter = React.forwardRef<HTMLDivElement, CardFooterProps>(({
  justify = 'start',
  className,
  children,
  ...props
}, ref) => {
  const justifyStyles = {
    start: 'justify-start',
    center: 'justify-center',
    end: 'justify-end',
    between: 'justify-between',
  }

  return (
    <div
      ref={ref}
      className={cn(
        'flex items-center gap-3 pt-4 border-t border-secondary-200 dark:border-secondary-700',
        justifyStyles[justify],
        className
      )}
      {...props}
    >
      {children}
    </div>
  )
})

CardFooter.displayName = 'CardFooter'

// Stats Card - Specialized card for statistics
interface StatsCardProps extends Omit<CardProps, 'variant' | 'children'> {
  title: string
  value: string | number
  change?: {
    value: number
    type: 'increase' | 'decrease'
  }
  icon?: React.ReactNode
  color?: 'primary' | 'success' | 'warning' | 'error' | 'info'
  subtitle?: string
}

export const StatsCard = React.forwardRef<HTMLDivElement, StatsCardProps>(({
  title,
  value,
  change,
  icon,
  color = 'primary',
  subtitle,
  className,
  ...props
}, ref) => {
  const colorStyles = {
    primary: 'from-primary-500 to-primary-600',
    success: 'from-success-500 to-success-600',
    warning: 'from-warning-500 to-warning-600',
    error: 'from-error-500 to-error-600',
    info: 'from-info-500 to-info-600',
  }

  const changeStyles = {
    increase: 'text-success-600 dark:text-success-400',
    decrease: 'text-error-600 dark:text-error-400',
  }

  return (
    <Card
      ref={ref}
      variant="gradient"
      className={cn('hover:shadow-lg transition-shadow duration-300', className)}
      {...props}
    >
      <div className="flex items-start justify-between">
        <div className="flex-1">
          <p className="text-sm font-medium text-secondary-500 dark:text-secondary-400 mb-1">
            {title}
          </p>
          <div className="flex items-baseline gap-2">
            <h4 className="text-3xl font-bold text-secondary-900 dark:text-white">
              {value}
            </h4>
            {change && (
              <span className={cn('text-sm font-medium flex items-center gap-1', changeStyles[change.type])}>
                {change.type === 'increase' ? '↑' : '↓'}
                {Math.abs(change.value)}%
              </span>
            )}
          </div>
          {subtitle && (
            <p className="text-sm text-secondary-500 dark:text-secondary-400 mt-2">
              {subtitle}
            </p>
          )}
        </div>
        {icon && (
          <div className={cn(
            'w-12 h-12 rounded-xl flex items-center justify-center',
            'bg-gradient-to-br',
            colorStyles[color],
            'text-white shadow-lg'
          )}>
            <div className="w-6 h-6">{icon}</div>
          </div>
        )}
      </div>
    </Card>
  )
})

StatsCard.displayName = 'StatsCard'

// Metric Card - For showing metrics with progress
interface MetricCardProps extends Omit<CardProps, 'variant' | 'children'> {
  title: string
  value: string | number
  target: string | number
  progress: number // 0-100
  status?: 'success' | 'warning' | 'error' | 'info'
  unit?: string
}

export const MetricCard = React.forwardRef<HTMLDivElement, MetricCardProps>(({
  title,
  value,
  target,
  progress,
  status = 'info',
  unit,
  className,
  ...props
}, ref) => {
  const statusColors = {
    success: 'bg-success-500',
    warning: 'bg-warning-500',
    error: 'bg-error-500',
    info: 'bg-info-500',
  }

  const statusTextColors = {
    success: 'text-success-600 dark:text-success-400',
    warning: 'text-warning-600 dark:text-warning-400',
    error: 'text-error-600 dark:text-error-400',
    info: 'text-info-600 dark:text-info-400',
  }

  return (
    <Card
      ref={ref}
      className={cn('hover:shadow-md', className)}
      {...props}
    >
      <div className="space-y-4">
        <div className="flex items-center justify-between">
          <h4 className="font-medium text-secondary-900 dark:text-white">{title}</h4>
          <span className={cn('text-sm font-medium', statusTextColors[status])}>
            {progress.toFixed(1)}%
          </span>
        </div>

        <div className="space-y-2">
          <div className="flex items-baseline justify-between">
            <span className="text-2xl font-bold text-secondary-900 dark:text-white">
              {value}
              {unit && <span className="text-sm font-normal ml-1">{unit}</span>}
            </span>
            <span className="text-sm text-secondary-500 dark:text-secondary-400">
              Target: {target}
            </span>
          </div>

          <div className="h-2 bg-secondary-200 dark:bg-secondary-700 rounded-full overflow-hidden">
            <div
              className={cn('h-full rounded-full transition-all duration-500', statusColors[status])}
              style={{ width: `${Math.min(100, progress)}%` }}
            />
          </div>
        </div>
      </div>
    </Card>
  )
})

MetricCard.displayName = 'MetricCard'

export { Card, CardHeader, CardContent, CardFooter }
export default Card
